import { useMemo, useState } from 'preact/hooks';
import { mensagemDoErro, qs } from '../api';
import { ROTULO_NATUREZA } from '../componentes/CartaoIndicador';
import { DrawerMunicipio } from '../componentes/DrawerMunicipio';
import { formatarIndicador, inteiro, pct } from '../format';
import { useApi } from '../hooks';
import { pode } from '../permissoes';
import { irPara } from '../router';
import { municipioSel, usuario } from '../state';
import type { EixoInfo, Impacto, IndicadorPanorama, Situacao } from '../types';
import { Barra, Carregando, ErroForm, Selo } from '../ui';

const SITUACAO: Record<Situacao, { texto: string; tom: 'verde' | 'azul' | 'ambar' | 'vermelho' | 'cinza'; explica: string }> = {
  LACUNA: { texto: 'Lacuna', tom: 'vermelho', explica: 'Necessidade alta e nenhuma ação cadastrada' },
  ATENCAO: { texto: 'Atenção', tom: 'ambar', explica: 'Necessidade alta; há ação, mas o progresso ainda é baixo' },
  EM_ACAO: { texto: 'Em ação', tom: 'verde', explica: 'Necessidade alta e as ações estão avançando' },
  ESTAVEL: { texto: 'Estável', tom: 'cinza', explica: 'Fora do pior pedaço do estado neste eixo' },
  SEM_INDICE: { texto: 'Sem índice', tom: 'cinza', explica: 'Faltam dados para calcular o índice' },
  SEM_MAPEAMENTO: { texto: 'Atuação não mapeada', tom: 'cinza', explica: 'Há necessidade, mas nenhuma secretaria está ligada a este eixo' },
};

const corIndice = (p: number) => (p < 25 ? 'var(--vermelho)' : p < 50 ? 'var(--ambar)' : p < 75 ? 'var(--lima)' : 'var(--verde)');
const POR_PAGINA = 40;

export function ImpactoPage() {
  const podeMapear = pode(usuario.value?.perfil, 'secretaria:write');
  const eixos = useApi<EixoInfo[]>('/indicadores/eixos');
  const medem = (eixos.dados ?? []).filter((e) => e.medemDesempenho > 0);

  const [eixoEscolhido, setEixo] = useState('');
  const eixo = eixoEscolhido || medem[0]?.slug || '';
  const [limiar, setLimiar] = useState(40);
  const [filtro, setFiltro] = useState<Situacao | ''>('');
  const [meso, setMeso] = useState('');
  const [busca, setBusca] = useState('');
  const [mostrar, setMostrar] = useState(POR_PAGINA);

  const impacto = useApi<Impacto>(eixo ? `/indicadores/impacto${qs({ eixo, limiar })}` : null);
  const panorama = useApi<IndicadorPanorama[]>(eixo ? `/indicadores${qs({ eixo })}` : null);
  const d = impacto.dados && impacto.dados.eixo.slug === eixo ? impacto.dados : null;

  const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const mesorregioes = useMemo(() => [...new Set((d?.municipios ?? []).map((m) => m.mesorregiao))].sort(), [d]);
  const linhas = useMemo(
    () => (d?.municipios ?? []).filter((m) => (!filtro || m.situacao === filtro) && (!meso || m.mesorregiao === meso) && (!busca || norm(m.municipio).includes(norm(busca)))),
    [d, filtro, meso, busca],
  );

  const cartao = (s: Situacao, n: number) => (
    <button type="button" key={s} class={`cartao-situacao tom-${SITUACAO[s].tom}${filtro === s ? ' ativo' : ''}`} aria-pressed={filtro === s} onClick={() => { setFiltro(filtro === s ? '' : s); setMostrar(POR_PAGINA); }}>
      <span class="cartao-situacao-n">{inteiro(n)}</span>
      <span class="cartao-situacao-t">{SITUACAO[s].texto}</span>
      <small>{SITUACAO[s].explica}</small>
    </button>
  );

  return (
    <main class="pagina">
      <header class="pagina-topo">
        <div>
          <h1>Impacto</h1>
          <p class="dica">Onde a realidade está pior segundo os dados oficiais, e onde o governo está atuando. Cruza indicadores do IBGE, da Saúde e do Tesouro com as ações cadastradas.</p>
        </div>
      </header>

      {eixos.erro && <ErroForm texto={mensagemDoErro(eixos.erro)} />}
      {eixos.carregando && !eixos.dados && <Carregando />}

      <div class="abas abas-eixo" role="tablist" aria-label="Eixo de política pública">
        {medem.map((e) => (
          <button key={e.slug} type="button" role="tab" aria-selected={eixo === e.slug} class={eixo === e.slug ? 'aba ativa' : 'aba'} onClick={() => { setEixo(e.slug); setFiltro(''); setMeso(''); setBusca(''); setMostrar(POR_PAGINA); }}>
            <i class="ponto" style={{ background: e.cor }} />{e.nome}
          </button>
        ))}
      </div>

      {impacto.erro && <ErroForm texto={mensagemDoErro(impacto.erro)} />}
      {impacto.carregando && !d && <Carregando />}

      {d && (
        <>
          {d.semSecretarias && (
            <p class="aviso" role="note">
              <strong>Nenhuma secretaria está ligada ao eixo {d.eixo.nome}.</strong> Sem isso não dá para saber quem atua onde, então os municípios com necessidade aparecem como
              "atuação não mapeada" (e não como lacuna).{' '}
              {podeMapear ? <a href="#/secretarias" onClick={(e) => { e.preventDefault(); irPara('secretarias'); }}>Ligar uma secretaria a este eixo →</a> : 'Peça a um gestor para ligar a secretaria correspondente.'}
            </p>
          )}

          <section class="cartoes-situacao" aria-label="Resumo do eixo">
            {d.semSecretarias ? cartao('SEM_MAPEAMENTO', d.resumo.semMapeamento) : cartao('LACUNA', d.resumo.lacuna)}
            {cartao('ATENCAO', d.resumo.atencao)}
            {cartao('EM_ACAO', d.resumo.emAcao)}
            {cartao('ESTAVEL', d.resumo.estavel)}
          </section>

          <details class="como-ler">
            <summary>Como este índice é calculado</summary>
            <p>
              O <strong>índice do eixo</strong> vai de 0 a 100 e é a <strong>média da posição relativa</strong> do município nos indicadores de <em>resultado</em> e de <em>estrutura</em> abaixo
              (0 = pior da Paraíba, 100 = melhor). Indicadores de gasto e de contexto aparecem, mas não entram na conta.
              {d.indiceMedioEstado !== null && <> O índice médio do estado é {inteiro(Math.round(d.indiceMedioEstado))}.</>}
            </p>
            <p>
              <strong>Atenção:</strong> é um ranking <em>entre os municípios</em>, não uma comparação com uma meta. Quando quase todos estão perto de 100% (como a frequência escolar de 6 a 14 anos),
              diferenças pequenas movem muito a posição. Por isso use o índice para achar onde olhar, e confira o valor real no painel do município.
              O cruzamento com as ações considera apenas as secretarias ligadas ao eixo ({d.secretariasDoEixo.length ? d.secretariasDoEixo.map((s) => s.sigla).join(', ') : 'nenhuma'}); ações canceladas não contam.
            </p>
            <ul class="chips-ind">
              {d.mede.map((m) => <li key={m.slug}>{m.nome} <small>({m.periodo ?? '—'})</small></li>)}
            </ul>
          </details>

          <section class="barra-filtros" aria-label="Filtros">
            <label class="campo"><span>Necessidade alta = índice abaixo de</span>
              <select value={limiar} onChange={(e) => { setLimiar(Number(e.currentTarget.value)); setMostrar(POR_PAGINA); }}>
                {[25, 33, 40, 50].map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <label class="campo"><span>Mesorregião</span>
              <select value={meso} onChange={(e) => { setMeso(e.currentTarget.value); setMostrar(POR_PAGINA); }}>
                <option value="">Todas</option>
                {mesorregioes.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label class="campo"><span>Buscar município</span><input type="search" value={busca} onInput={(e) => { setBusca(e.currentTarget.value); setMostrar(POR_PAGINA); }} placeholder="Digite o nome…" /></label>
            {(filtro || meso || busca) && <button type="button" class="link" onClick={() => { setFiltro(''); setMeso(''); setBusca(''); }}>Limpar filtros</button>}
          </section>

          <div class="tabela-rolagem">
            <table class="tabela">
              <caption class="so-leitor">Necessidade e atuação por município, do pior índice para o melhor</caption>
              <thead>
                <tr><th scope="col">Município</th><th scope="col">Mesorregião</th><th scope="col" class="col-prog">Índice do eixo</th><th scope="col" class="num">Ações</th><th scope="col" class="num">Progresso</th><th scope="col">Situação</th></tr>
              </thead>
              <tbody>
                {linhas.slice(0, mostrar).map((m) => (
                  <tr key={m.codIbge}>
                    <td><button type="button" class="link-linha" onClick={() => (municipioSel.value = m.codIbge)}>{m.municipio}</button></td>
                    <td>{m.mesorregiao.replace(' Paraibano', '').replace(' Paraibana', '')}</td>
                    <td class="col-prog">
                      {m.indice === null ? <span class="dica">—</span> : <div class="prog-celula"><Barra pct={m.indice} cor={corIndice(m.indice)} /><span>{Math.round(m.indice)}</span></div>}
                    </td>
                    <td class="num">{d.semSecretarias ? '—' : m.acoes}</td>
                    <td class="num">{m.progressoAcoes === null ? '—' : pct(m.progressoAcoes)}</td>
                    <td><span title={SITUACAO[m.situacao].explica}><Selo texto={SITUACAO[m.situacao].texto} tom={SITUACAO[m.situacao].tom} /></span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {linhas.length === 0 && <p class="vazio-grande">Nenhum município com esses filtros.</p>}
          </div>
          <footer class="rodape-lista">
            <span class="dica">{inteiro(Math.min(mostrar, linhas.length))} de {inteiro(linhas.length)} municípios</span>
            {mostrar < linhas.length && <button type="button" class="botao" onClick={() => setMostrar(mostrar + POR_PAGINA)}>Mostrar mais</button>}
          </footer>
        </>
      )}

      {panorama.dados && panorama.dados.length > 0 && (
        <section class="bloco panorama" aria-label="Indicadores do eixo">
          <h2>Indicadores do eixo</h2>
          <div class="tabela-rolagem">
            <table class="tabela">
              <caption class="so-leitor">Situação do estado em cada indicador do eixo</caption>
              <thead><tr><th scope="col">Indicador</th><th scope="col">Tipo</th><th scope="col" class="num">Mediana da PB</th><th scope="col">Pior</th><th scope="col">Melhor</th><th scope="col">Fonte</th></tr></thead>
              <tbody>
                {panorama.dados.map((i) => {
                  const nat = ROTULO_NATUREZA[i.natureza];
                  const f = (v: number) => formatarIndicador(v, i.unidade, i.casas);
                  const neutro = i.sentido === 'NEUTRO';
                  return (
                    <tr key={i.slug}>
                      <td><strong>{i.nome}</strong><small class="sub">{i.descricao}</small></td>
                      <td><span title={nat.ajuda}><Selo texto={nat.texto} tom={nat.tom} /></span></td>
                      <td class="num">{i.estado ? f(i.estado.mediana) : '—'}</td>
                      <td>{i.pior ? <><button type="button" class="link-linha" onClick={() => (municipioSel.value = i.pior!.codIbge)}>{i.pior.municipio}</button><small class="sub">{neutro ? 'menor valor: ' : ''}{f(i.pior.valor)}</small></> : '—'}</td>
                      <td>{i.melhor ? <><button type="button" class="link-linha" onClick={() => (municipioSel.value = i.melhor!.codIbge)}>{i.melhor.municipio}</button><small class="sub">{neutro ? 'maior valor: ' : ''}{f(i.melhor.valor)}</small></> : '—'}</td>
                      <td><a href={i.fonte.url} target="_blank" rel="noopener noreferrer">{i.fonte.slug.toUpperCase()}</a><small class="sub">{i.periodo ?? '—'}</small></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {municipioSel.value && <DrawerMunicipio cod={municipioSel.value} data={undefined} onFechar={() => (municipioSel.value = null)} />}
    </main>
  );
}
